import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Download, Eye, Palette, Layout, X, CheckCircle } from 'lucide-react';
import { getStoredCVInfo } from '../utils/cvDataExtractor';
import type { FinalResults } from '../types';

interface CVTemplateGeneratorProps {
  results: FinalResults;
  isOpen: boolean;
  onClose: () => void;
}

const CVTemplateGenerator: React.FC<CVTemplateGeneratorProps> = ({
  results,
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCV, setGeneratedCV] = useState<string | null>(null);

  if (!isOpen) return null;

  const personalInfo = getStoredCVInfo();

  const templates = [
    {
      id: 'modern',
      name: t('cv_template.templates.modern.name'),
      description: t('cv_template.templates.modern.description'),
      preview: t('cv_template.templates.modern.preview'),
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'classic',
      name: t('cv_template.templates.classic.name'),
      description: t('cv_template.templates.classic.description'),
      preview: t('cv_template.templates.classic.preview'),
      color: 'from-gray-500 to-gray-600'
    },
    {
      id: 'creative',
      name: t('cv_template.templates.creative.name'),
      description: t('cv_template.templates.creative.description'),
      preview: t('cv_template.templates.creative.preview'),
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'tech',
      name: t('cv_template.templates.tech.name'),
      description: t('cv_template.templates.tech.description'),
      preview: t('cv_template.templates.tech.preview'),
      color: 'from-green-500 to-teal-500'
    }
  ];

  const generateOptimizedCV = async () => {
    setIsGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 3000));
    const optimizedCV = createOptimizedCVContent();
    setGeneratedCV(optimizedCV);
    setIsGenerating(false);
  };

  const createOptimizedCVContent = (): string => {
    const skills = results.analysisResults.skills;
    const experience = results.analysisResults.experience;
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${t('cv_template.generated.title', { name: personalInfo.name || t('cv_template.generated.default_name') })}</title>
    <style>
        body { font-family: 'Arial', sans-serif; margin: 0; padding: 20px; color: #333; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; text-align: center; }
        .name { font-size: 2.5em; font-weight: bold; margin-bottom: 10px; }
        .title { font-size: 1.2em; opacity: 0.9; }
        .contact { margin-top: 15px; }
        .section { margin: 30px 0; }
        .section-title { font-size: 1.4em; font-weight: bold; color: #667eea; border-bottom: 2px solid #667eea; padding-bottom: 5px; margin-bottom: 15px; }
        .skills-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
        .skill-item { background: #f8f9fa; padding: 10px; border-radius: 5px; border-left: 4px solid #667eea; }
        .experience-item { margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; }
        .job-title { font-weight: bold; color: #333; }
        .company { color: #667eea; font-weight: 500; }
        .date { color: #666; font-size: 0.9em; }
        .achievement { margin: 5px 0; padding-left: 15px; position: relative; }
        .achievement:before { content: "▶"; color: #667eea; position: absolute; left: 0; }
    </style>
</head>
<body>
    <div class="header">
        <div class="name">${personalInfo.name || t('cv_template.generated.default_name')}</div>
        <div class="title">${t('cv_template.generated.professional_title')}</div>
        <div class="contact">
            📧 ${personalInfo.email || t('cv_template.generated.default_email')} | 
            📱 ${personalInfo.phone || t('cv_template.generated.default_phone')} | 
            💼 ${personalInfo.linkedin || 'LinkedIn'} | 
            🌐 ${personalInfo.website || t('cv_template.generated.default_website')}
        </div>
    </div>

    <div class="section">
        <div class="section-title">🎯 ${t('cv_template.generated.profile_section')}</div>
        <p>${t('cv_template.generated.profile_text', { score: results.globalScore })}</p>
    </div>

    <div class="section">
        <div class="section-title">💻 ${t('cv_template.generated.skills_section')}</div>
        <div class="skills-grid">
            ${skills?.found.map(skill => `
                <div class="skill-item">
                    <strong>${skill.found}</strong>
                    <div style="font-size: 0.9em; color: #666;">${t('cv_template.generated.level')} ${skill.level}</div>
                </div>
            `).join('') || ''}
        </div>
    </div>

    <div class="section">
        <div class="section-title">🏢 ${t('cv_template.generated.experience_section')}</div>
        ${experience?.relevantExperiences.map(exp => `
            <div class="experience-item">
                <div class="job-title">${exp.summary.title}</div>
                <div class="company">${exp.summary.details}</div>
                <div class="date">2021 - ${t('cv_template.generated.present')}</div>
                <div class="achievement">${t('cv_template.generated.achievement1')}</div>
                <div class="achievement">${t('cv_template.generated.achievement2')}</div>
                <div class="achievement">${t('cv_template.generated.achievement3')}</div>
            </div>
        `).join('') || ''}
    </div>

    <div class="section">
        <div class="section-title">🎓 ${t('cv_template.generated.education_section')}</div>
        <div class="experience-item">
            <div class="job-title">${t('cv_template.generated.education_title')}</div>
            <div class="company">${t('cv_template.generated.education_school')}</div>
            <div class="date">2017</div>
        </div>
    </div>

    <div class="section">
        <div class="section-title">🌍 ${t('cv_template.generated.languages_section')}</div>
        <div class="skills-grid">
            <div class="skill-item"><strong>${t('cv_template.generated.language_french')}</strong><br>${t('cv_template.generated.language_french_level')}</div>
            <div class="skill-item"><strong>${t('cv_template.generated.language_english')}</strong><br>${t('cv_template.generated.language_english_level')}</div>
            <div class="skill-item"><strong>${t('cv_template.generated.language_spanish')}</strong><br>${t('cv_template.generated.language_spanish_level')}</div>
        </div>
    </div>

    <footer style="margin-top: 40px; text-align: center; color: #666; font-size: 0.8em;">
        ${t('cv_template.generated.footer', { score: results.globalScore })}
    </footer>
</body>
</html>`;
  };

  const downloadCV = () => {
    if (!generatedCV) return;
    const blob = new Blob([generatedCV], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `CV_Optimise_${personalInfo.name?.replace(/\s+/g, '_') || t('cv_template.generated.default_name')}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <FileText className="w-6 h-6 text-indigo-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">📄 {t('cv_template.title')}</h2>
              <p className="text-gray-600 mt-1">{t('cv_template.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {!generatedCV ? (
            <>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('cv_template.choose_template')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedTemplate === template.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedTemplate(template.id)}
                    >
                      <div className={`inline-flex p-2 rounded-lg bg-gradient-to-r ${template.color} text-white mb-3`}>
                        <Layout className="w-5 h-5" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
                      <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                      <p className="text-xs text-gray-500">{template.preview}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">🎯 {t('cv_template.optimizations_included')}</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✅ {t('cv_template.optimization.keywords', { score: results.globalScore })}</li>
                  <li>✅ {t('cv_template.optimization.skills')}</li>
                  <li>✅ {t('cv_template.optimization.ats')}</li>
                  <li>✅ {t('cv_template.optimization.format')}</li>
                  <li>✅ {t('cv_template.optimization.prefilled')}</li>
                </ul>
              </div>

              <button
                onClick={generateOptimizedCV}
                disabled={isGenerating}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isGenerating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {t('cv_template.generating')}
                  </>
                ) : (
                  <>
                    <Palette className="w-5 h-5 mr-2" />
                    {t('cv_template.generate_button')}
                  </>
                )}
              </button>
            </>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  {t('cv_template.generated.success')}
                </h3>
                <div className="flex space-x-3">
                  <button
                    onClick={() => window.open('data:text/html,' + encodeURIComponent(generatedCV), '_blank')}
                    className="flex items-center px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {t('cv_template.generated.preview')}
                  </button>
                  <button
                    onClick={downloadCV}
                    className="flex items-center px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('cv_template.generated.download')}
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">📋 {t('cv_template.generated.includes')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <ul className="space-y-1 text-gray-700">
                    <li>✅ {t('cv_template.generated.design', { template: templates.find(tpl => tpl.id === selectedTemplate)?.name })}</li>
                    <li>✅ {t('cv_template.generated.optimized_skills')}</li>
                    <li>✅ {t('cv_template.generated.relevant_experience')}</li>
                  </ul>
                  <ul className="space-y-1 text-gray-700">
                    <li>✅ {t('cv_template.generated.keywords')}</li>
                    <li>✅ {t('cv_template.generated.ats_format')}</li>
                    <li>✅ {t('cv_template.generated.displayed_score')}</li>
                  </ul>
                </div>
              </div>

              <button
                onClick={() => {
                  setGeneratedCV(null);
                  setSelectedTemplate('modern');
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg transition-colors"
              >
                {t('cv_template.generate_new')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CVTemplateGenerator;